// Locale helpers that don't need i18next, so `ui` can format with them and
// the i18n setup in `shared` builds on them.

export const FALLBACK = "en"; // one default, used everywhere

export function detectLocales(): string {
  const raw =
    typeof navigator === "undefined"
      ? []
      : navigator.languages?.length
        ? [...navigator.languages]
        : [navigator.language].filter(Boolean);

  if (raw.length === 0) {
    return FALLBACK;
  }

  return raw[0];
}

let cached: string | undefined;

// The full regional locale used for date/number formatting.
export const getFullLocale = (): string => (cached ??= detectLocales());

/**
 * The codes a brand language can be built on for `locale`, most specific
 * first: `es-US` gives `es_us`, `es`, then `en`. A brand language is
 * `<base>_<brand>`, such as `en_us_peac_us_inc`.
 */
export const brandBases = (locale: string): string[] => {
  const code = locale.replace(/-/g, "_").toLowerCase();
  return [...new Set([code, code.split("_")[0], FALLBACK])];
};

// What follows a base in a topi code like `en_GB` or `es_419`.
const REGION = /^([a-z]{2}|\d{3})$/;

/** The base `language` was built on, or `undefined` if it isn't a brand's. */
export const brandBaseOf = (
  language: string,
  locale: string = getFullLocale()
): string | undefined => {
  // i18next hands cached formats the code with hyphens.
  const code = language.replace(/-/g, "_").toLowerCase();
  return brandBases(locale).find(
    (base) =>
      code.startsWith(`${base}_`) && !REGION.test(code.slice(base.length + 1))
  );
};

/**
 * A locale Intl accepts for `language`. Intl throws on a brand language, so
 * a brand formats in the browser locale.
 */
export const formatLocaleFor = (
  language: string,
  locale: string = getFullLocale()
): string =>
  (brandBaseOf(language, locale) ? locale : language).replace(/_/g, "-");

/**
 * The language part of an i18next language, lowercased: `de-DE` and
 * `de_peac_us_inc` give `de`. A brand language holds the brand's short name,
 * so check this rather than the whole code.
 */
export const languageOf = (language?: string): string | undefined =>
  language?.split(/[-_]/)[0].toLowerCase();
